import { useMemo } from 'react';
import { useAppSelector } from '../app/hooks';
import { selectTopLevelMenuItems } from '../selectors/menu';
import { useReminder } from '../contexts/ReminderContext';
import { TTS_PROMPT, TTS_PROMPT_PREFIX } from '../constants';
import { SLASH_COMMAND_REMINDERS } from '../utils/constants';
import { SlashCommand } from '../types/menu';

interface SlashCommandReminderResult {
  hasRequiredCommands: boolean;
  availableSlashCommands: SlashCommand[];
  showSlashCommandReminder: () => void;
}

/**
 * Custom hook that provides slash command reminder functionality.
 * Detects available slash commands from TTS prompt items and provides
 * a function to show the appropriate reminder message.
 */
export const useSlashCommandReminder = (): SlashCommandReminderResult => {
  const topLevelMenuItems = useAppSelector(selectTopLevelMenuItems) || {};
  const { showReminder } = useReminder();

  // Get the actual slash commands from tts-prompt category items
  const { hasRequiredCommands, availableSlashCommands } = useMemo(() => {
    const ttsPromptItems = Object.values(topLevelMenuItems).filter(
      (item) =>
        item.category.includes(TTS_PROMPT) ||
        item.category.includes(TTS_PROMPT_PREFIX)
    );

    // Filter items that have the required commands and they're active
    const relevantItems = ttsPromptItems.filter((item) => {
      if (!item.inactive && item.name) {
        const [intent] = item.name.split(':');
        // Exact match for donation and customer_info, prefix match for upsell_end_of_order
        return (
          [
            SLASH_COMMAND_REMINDERS.DONATION,
            SLASH_COMMAND_REMINDERS.CUSTOMER_INFO,
          ].includes(intent) ||
          intent.startsWith(SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX)
        );
      }
      return false;
    });

    // Sort items to ensure upsell_end_of_order comes first, then donation, then customer_info
    const sortedItems = relevantItems.sort((a, b) => {
      const aIntent = a.name?.split(':')[0] || '';
      const bIntent = b.name?.split(':')[0] || '';

      const aIsDonation = aIntent === SLASH_COMMAND_REMINDERS.DONATION;
      const bIsDonation = bIntent === SLASH_COMMAND_REMINDERS.DONATION;
      const aIsCustomerInfo = aIntent === SLASH_COMMAND_REMINDERS.CUSTOMER_INFO;
      const bIsCustomerInfo = bIntent === SLASH_COMMAND_REMINDERS.CUSTOMER_INFO;
      const aIsUpsellEndOfOrder = aIntent.startsWith(
        SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
      );
      const bIsUpsellEndOfOrder = bIntent.startsWith(
        SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
      );

      // Upsell end of order items come first
      if (aIsUpsellEndOfOrder && !bIsUpsellEndOfOrder) return -1;
      if (!aIsUpsellEndOfOrder && bIsUpsellEndOfOrder) return 1;

      // Donation items come after upsell_end_of_order
      if (aIsDonation && !bIsDonation && !bIsUpsellEndOfOrder) return -1;
      if (!aIsDonation && bIsDonation && !aIsUpsellEndOfOrder) return 1;

      // Customer info items come after donation
      if (
        aIsCustomerInfo &&
        !bIsCustomerInfo &&
        !bIsDonation &&
        !bIsUpsellEndOfOrder
      )
        return -1;
      if (
        !aIsCustomerInfo &&
        bIsCustomerInfo &&
        !aIsDonation &&
        !aIsUpsellEndOfOrder
      )
        return 1;

      // Other items maintain their relative order
      return 0;
    });

    // Extract commands from sorted items
    const commands: SlashCommand[] = [];
    sortedItems.forEach((item) => {
      if (item.slashCommands && item.slashCommands.length > 0) {
        commands.push({ intent: item.name, command: item.slashCommands[0] });
      } else {
        // Handle PRP menu setup
        const [intent, command] = item.name.split(':');
        if (command) {
          commands.push({ intent, command });
        }
      }
    });

    return {
      hasRequiredCommands: relevantItems.length > 0,
      availableSlashCommands: commands,
    };
  }, [topLevelMenuItems]);

  /**
   * Shows the appropriate slash command reminder message.
   * Only shows reminder if required commands exist in tts-prompt category.
   */
  const showSlashCommandReminder = () => {
    if (hasRequiredCommands) {
      // Check if we have upsell_end_of_order items
      const hasUpsellEndOfOrder = availableSlashCommands.some((cmd) =>
        cmd.intent.startsWith(
          SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
        )
      );
      const standardCommands = availableSlashCommands.filter(
        (cmd) =>
          !cmd.intent.startsWith(
            SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
          )
      );

      // Build message for standard commands (donation/customer-info)
      let message = '';
      if (standardCommands.length > 0) {
        message = 'Remember to ask for ';
        if (standardCommands.length === 1) {
          message +=
            standardCommands[0].intent === SLASH_COMMAND_REMINDERS.DONATION
              ? 'donation'
              : standardCommands[0].intent ===
                SLASH_COMMAND_REMINDERS.CUSTOMER_INFO
              ? 'customer name'
              : '';
        } else {
          message += 'donation and customer name';
        }
      }

      // Append end of order upsell if it exists
      if (hasUpsellEndOfOrder) {
        if (message) {
          message += ' and end of order upsell';
        } else {
          message = 'Remember to ask for end of order upsell';
        }
      }

      if (message) {
        showReminder(message, availableSlashCommands);
      }
    }
  };

  return {
    hasRequiredCommands,
    availableSlashCommands,
    showSlashCommandReminder,
  };
};

export default useSlashCommandReminder;
